import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Button, EmptyState, QueryStateBoundary } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import { useContact } from "@/api/hooks/useContacts";
import {
  useCompleteEnrollment,
  usePromoteEnrollment,
  useSectionRoster,
  useWithdrawEnrollment,
} from "@/api/hooks/useClassRegistration";
import {
  AGE_BAND_LABELS,
  ENROLLMENT_STATUS_BADGE_VARIANT,
  ENROLLMENT_STATUS_LABELS,
  type Enrollment,
} from "@/api/types/classRegistration";
import { formatCapacity, formatWeekdaySlot, sectionDisplayName } from "./classFormat";
import { EnrollmentDetailDrawer } from "./EnrollmentDetailDrawer";

/**
 * Section roster — active + waitlisted enrollees for one class section,
 * with the withdraw/promote/complete lifecycle actions
 * (app/services/class_registration.py). Row click opens
 * EnrollmentDetailDrawer, which carries the compliance consent-audit
 * trail.
 */
export function SectionRosterPage() {
  const { id } = useParams<{ id: string }>();
  const { data: roster, isLoading, error, refetch } = useSectionRoster(id);
  const [detailId, setDetailId] = useState<string | null>(null);

  const section = roster?.section;

  return (
    <div className="p-6 sm:p-8">
      <Link to="/classes" className="mb-4 inline-block text-sm text-text-secondary hover:underline">
        ← Back to classes
      </Link>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {roster && section && (
          <>
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="font-display text-2xl font-bold text-text-primary">{sectionDisplayName(section)}</h1>
                <p className="mt-1 text-sm text-text-secondary">
                  {formatWeekdaySlot(section.weekday, section.slot)}
                  {section.age_band ? ` · ${AGE_BAND_LABELS[section.age_band]}` : ""}
                  {" · "}
                  {centsToUsd(section.price_cents_monthly)}/mo
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="default">{formatCapacity(section.active_enrollment_count, section.cap)} enrolled</Badge>
                {section.below_minimum && (
                  <Badge variant="warning" data-testid="roster-below-minimum">
                    Below minimum ({section.min_enrollment})
                  </Badge>
                )}
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <RosterList
                title="Active"
                enrollments={roster.active}
                sectionCap={section.cap}
                sectionActiveCount={section.active_enrollment_count}
                onSelect={setDetailId}
              />
              <RosterList
                title="Waitlisted"
                enrollments={roster.waitlisted}
                sectionCap={section.cap}
                sectionActiveCount={section.active_enrollment_count}
                onSelect={setDetailId}
                isWaitlist
              />
            </div>
          </>
        )}
      </QueryStateBoundary>

      <EnrollmentDetailDrawer enrollmentId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function RosterList({
  title,
  enrollments,
  sectionCap,
  sectionActiveCount,
  onSelect,
  isWaitlist = false,
}: {
  title: string;
  enrollments: Enrollment[];
  sectionCap: number;
  sectionActiveCount: number;
  onSelect: (id: string) => void;
  isWaitlist?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-card">
      <div className="border-b border-border px-4 py-3">
        <h2 className="font-display text-base font-semibold text-text-primary">
          {title} ({enrollments.length})
        </h2>
      </div>
      {enrollments.length === 0 ? (
        <EmptyState
          title={isWaitlist ? "No one waitlisted" : "No active enrollees"}
          description={isWaitlist ? "Families land here once the section fills up." : "Register a family to fill this section."}
          className="py-8"
        />
      ) : (
        <ul className="divide-y divide-border" data-testid={isWaitlist ? "waitlist-list" : "active-roster-list"}>
          {enrollments.map((e) => (
            <EnrollmentRow
              key={e.id}
              enrollment={e}
              onSelect={onSelect}
              atCapacity={sectionActiveCount >= sectionCap}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EnrollmentRow({
  enrollment,
  onSelect,
  atCapacity,
}: {
  enrollment: Enrollment;
  onSelect: (id: string) => void;
  atCapacity: boolean;
}) {
  const { data: guardian } = useContact(enrollment.guardian_contact_id);
  const withdrawEnrollment = useWithdrawEnrollment();
  const promoteEnrollment = usePromoteEnrollment();
  const completeEnrollment = useCompleteEnrollment();

  const isTerminal = enrollment.status === "withdrawn" || enrollment.status === "completed";

  async function handleWithdraw(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await withdrawEnrollment.mutateAsync(enrollment.id);
      toastSuccess("Enrollment withdrawn");
    } catch (err) {
      toastError("Couldn't withdraw enrollment", getErrorMessage(err));
    }
  }

  // The section-at-capacity check above is a best-effort UX hint only —
  // the button always fires the request (server is the actual source of
  // truth on capacity) and a genuine 409 race surfaces as a toast rather
  // than being silently swallowed by a hard client-side block.
  async function handlePromote(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await promoteEnrollment.mutateAsync(enrollment.id);
      toastSuccess("Enrollment promoted off the waitlist");
    } catch (err) {
      toastError("Couldn't promote enrollment", getErrorMessage(err));
    }
  }

  async function handleComplete(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await completeEnrollment.mutateAsync(enrollment.id);
      toastSuccess("Enrollment marked completed");
    } catch (err) {
      toastError("Couldn't complete enrollment", getErrorMessage(err));
    }
  }

  // NOTE: this row's "click to open detail" affordance is a <div
  // role="button">, NOT a real <button> — the Withdraw/Promote/Complete
  // actions below are real <button> elements (via the shared Button
  // component), and a <button> can never validly contain another
  // <button> (invalid HTML — React logs a hydration-nesting error for it,
  // and nested-interactive-element behavior is undefined across
  // browsers/assistive tech). A keyboard-activatable div preserves the
  // same click/keyboard "open detail" behavior without nesting.
  function handleRowKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(enrollment.id);
    }
  }

  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(enrollment.id)}
        onKeyDown={handleRowKeyDown}
        className="flex w-full cursor-pointer flex-col gap-2 px-4 py-3 text-left hover:bg-bg-hover sm:flex-row sm:items-center sm:justify-between"
        data-testid={`enrollment-row-${enrollment.id}`}
      >
        <div>
          <p className="font-medium text-text-primary">
            {enrollment.child_first_name} {enrollment.child_last_name}
            {enrollment.waitlist_position != null && (
              <span className="ml-2 text-xs text-text-muted">#{enrollment.waitlist_position}</span>
            )}
          </p>
          <p className="text-xs text-text-muted">{guardian?.name ?? "…"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={ENROLLMENT_STATUS_BADGE_VARIANT[enrollment.status]}>
            {ENROLLMENT_STATUS_LABELS[enrollment.status]}
          </Badge>
          {!isTerminal && (
            <Button size="sm" variant="secondary" onClick={handleWithdraw} disabled={withdrawEnrollment.isPending}>
              Withdraw
            </Button>
          )}
          {enrollment.status === "waitlisted" && (
            <Button
              size="sm"
              variant={atCapacity ? "outline" : "secondary"}
              onClick={handlePromote}
              disabled={promoteEnrollment.isPending}
              title={atCapacity ? "Section is at capacity — this may fail" : undefined}
            >
              Promote
            </Button>
          )}
          {enrollment.status === "active" && (
            <Button size="sm" onClick={handleComplete} disabled={completeEnrollment.isPending}>
              Complete
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
